import React, {CSSProperties, useCallback, useEffect, useRef, useState} from "react";
import {Dialog} from "@components/interface/Dialog";
import {selectDialog} from "@redux/selectors/dialog";
import {connect} from "react-redux";
import {IDialog, update as updateDialog} from "@redux/slices/dialog";
import {Update} from "@redux/slices/base";
import {COLOURS, THEME} from "@components/core/theme";
import {Game} from "@game/game";
import {GameEvent} from "@game/gameEvent";
import {Player} from "@game/player/player";

const SITE_ORIGIN = "https://wordland.uk";

interface LeagueRow {
    nickname: string
    nick_number: number
    committed: number
    total: number
    best_word: string | null
    best_word_score: number | null
    best_word_region_id: number | null
    best_word_tile_id: number | null
}

interface LeagueMeta {
    name: string
    subtitle: string
    description: string
}

interface LeagueData {
    league: LeagueMeta
    results: LeagueRow[]
}

interface ICompetition {
    id: string
    name: string
    invite_code: string
    ends_at: string
}

interface IStandingRow {
    nickname: string
    nick_number: number
    committed: number
    total: number
    best_word: string | null
    best_word_score: number | null
    best_word_region_id: number | null
    best_word_tile_id: number | null
}

type LeagueId = "monthly" | "annual" | "alltime";
type TabId = LeagueId | "myLeagues";
type CompTabId = "new" | string;

type IndexedRow = LeagueRow & {index: number};

const PUBLIC_TABS: {id: LeagueId; label: string; emptyMsg: string}[] = [
    {id: "monthly", label: "Daily",   emptyMsg: "No scores yet today."},
    {id: "annual",  label: "Weekly",  emptyMsg: "No scores yet this week."},
    {id: "alltime", label: "Monthly", emptyMsg: "No scores yet this month."},
];

const styles: Record<string, CSSProperties> = {
    body: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
    },
    tabs: {
        borderBottom: `2px solid ${COLOURS.agedBorder}`,
        display: "flex",
        flexDirection: "row" as const,
        marginBottom: 8,
    },
    tab: {
        cursor: "pointer",
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        margin: 4,
        padding: "4px 8px",
        color: COLOURS.amber,
        fontWeight: "normal" as const,
    },
    tabActive: {
        borderBottom: `3px solid ${COLOURS.forestGreen}`,
        color: COLOURS.forestGreen,
        marginBottom: -2,
        fontWeight: "bold" as const,
    },
    scroll: {
        flexGrow: 1,
        minHeight: 0,
        overflowY: "auto",
    },
    table: {
        borderCollapse: "collapse" as const,
        tableLayout: "fixed" as const,
        width: "100%",
    },
    th: {
        ...THEME.text,
        fontSize: "small",
        fontWeight: "bold",
        color: COLOURS.forestGreen,
        borderBottom: `2px solid ${COLOURS.agedBorder}`,
        padding: "0 4px 6px",
        textAlign: "left" as const,
    },
    td: {
        ...THEME.text,
        fontSize: "small",
        padding: "5px 4px",
        borderBottom: `1px solid ${COLOURS.agedBorder}`,
        whiteSpace: "nowrap" as const,
        overflow: "hidden",
    },
    tdTruncate: {
        textOverflow: "ellipsis",
    },
    currentRow: {
        backgroundColor: COLOURS.parchmentDark,
    },
    separator: {
        ...THEME.text,
        textAlign: "center" as const,
        padding: "6px 0",
        color: COLOURS.agedBorder,
        letterSpacing: 4,
        fontSize: "small",
    },
    empty: {
        ...THEME.text,
        padding: 12,
        color: COLOURS.amber,
        fontStyle: "italic",
    },
    // My Leagues sub-tabs
    compSubTabsWrapper: {
        alignItems: "flex-end",
        borderBottom: `1px solid ${COLOURS.agedBorder}`,
        display: "flex",
        marginBottom: 8,
    },
    compSubTabs: {
        display: "flex",
        flex: 1,
        flexWrap: "nowrap" as const,
        overflowX: "auto" as const,
        scrollbarWidth: "none" as any,
    },
    compScrollBtn: {
        alignSelf: "center",
        background: "transparent",
        border: "none",
        color: COLOURS.amber,
        cursor: "pointer",
        flexShrink: 0,
        fontSize: "large",
        lineHeight: 1,
        padding: "0 4px 6px",
    },
    compTab: {
        cursor: "pointer",
        flexShrink: 0,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontSize: "small",
        margin: "4px 2px",
        padding: "3px 10px",
        color: COLOURS.amber,
        fontWeight: "normal" as const,
        whiteSpace: "nowrap" as const,
    },
    compTabActive: {
        borderBottom: `3px solid ${COLOURS.forestGreen}`,
        color: COLOURS.forestGreen,
        marginBottom: -1,
        fontWeight: "bold" as const,
    },
    compFooter: {
        alignItems: "center",
        borderTop: `1px solid ${COLOURS.agedBorder}`,
        display: "flex",
        justifyContent: "space-between",
        paddingTop: 8,
        marginTop: 4,
    },
    compMetaText: {
        ...THEME.text,
        color: COLOURS.amber,
        fontSize: "small"
    },
    createForm: {
        display: "flex",
        flexDirection: "column" as const,
        gap: 8,
        padding: "4px 0",
    },
    createInput: {
        ...THEME.text,
        boxSizing: "border-box" as const,
        fontSize: "medium",
        padding: "4px 8px",
        width: "100%",
    },
    createError: {
        ...THEME.text,
        color: "red",
        fontSize: "small",
        fontStyle: "italic",
    },
};

interface ILeagueDialog {
    dialog: IDialog
    game: Game
    updateDialog: Update
}

function LeagueDialog({dialog, game, updateDialog}: ILeagueDialog) {
    // Public league state
    const [activeTab, setActiveTab] = useState<TabId>("monthly");
    const [cache, setCache] = useState<Partial<Record<LeagueId, LeagueData>>>({});
    const [loading, setLoading] = useState(false);

    // My Leagues state
    const [competitions, setCompetitions] = useState<ICompetition[]>([]);
    const [loadingComps, setLoadingComps] = useState(false);
    const [activeCompTab, setActiveCompTab] = useState<CompTabId>("new");
    const [standingsCache, setStandingsCache] = useState<Record<string, IStandingRow[]>>({});
    const [loadingStandings, setLoadingStandings] = useState(false);
    const [createName, setCreateName] = useState("");
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [newLeagueId, setNewLeagueId] = useState<string | null>(null);

    const prevName = useRef("");
    const tabBarRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButtons = useCallback(() => {
        const el = tabBarRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }, []);

    useEffect(() => {
        const el = tabBarRef.current;
        if (!el) return;
        updateScrollButtons();
        el.addEventListener("scroll", updateScrollButtons);
        const ro = new ResizeObserver(updateScrollButtons);
        ro.observe(el);
        return () => { el.removeEventListener("scroll", updateScrollButtons); ro.disconnect(); };
    }, [updateScrollButtons]);

    useEffect(() => {
        const id = setTimeout(updateScrollButtons, 0);
        return () => clearTimeout(id);
    }, [competitions, updateScrollButtons]);

    const scrollTabs = (dir: "left" | "right") => {
        tabBarRef.current?.scrollBy({left: dir === "left" ? -120 : 120, behavior: "smooth"});
    };

    // Reset state when dialog opens
    useEffect(() => {
        if (dialog.name === "league" && prevName.current !== "league") {
            setActiveTab(dialog.data?.tab ?? "monthly");
            setCache({});
            setCompetitions([]);
            setActiveCompTab("new");
            setStandingsCache({});
            setCreateName("");
            setCreateError(null);
            setNewLeagueId(null);
        }
        prevName.current = dialog.name;
    }, [dialog.name]);

    // Invalidate cache when the user scores so re-opening shows fresh data
    useEffect(() => {
        const handleEvent = (event: GameEvent) => {
            if (event.type === "PlayerScored") {
                setCache({});
                setStandingsCache({});
            }
        };
        game.events.delegates.add(handleEvent);
        return () => game.events.delegates.remove(handleEvent);
    }, [game]);

    // Fetch public league data
    useEffect(() => {
        if (dialog.name !== "league" || activeTab === "myLeagues") return;
        const leagueId = activeTab as LeagueId;
        if (cache[leagueId]) return;
        setLoading(true);
        game.getLeague(leagueId)
            .then(data => setCache(prev => ({...prev, [leagueId]: data})))
            .catch(err => console.error("[LeagueDialog] fetch error:", err))
            .finally(() => setLoading(false));
    }, [dialog.name, activeTab, cache]);

    // Fetch competitions list when My Leagues tab becomes active
    useEffect(() => {
        if (dialog.name !== "league" || activeTab !== "myLeagues") return;
        if (competitions.length > 0) return;
        setLoadingComps(true);
        game.getPlayerCompetitions()
            .then(res => setCompetitions(res.results))
            .catch(() => setCompetitions([]))
            .finally(() => setLoadingComps(false));
    }, [dialog.name, activeTab]);

    // Fetch standings when a competition sub-tab is selected
    useEffect(() => {
        if (activeTab !== "myLeagues" || activeCompTab === "new") return;
        if (standingsCache[activeCompTab]) return;
        setLoadingStandings(true);
        game.getCompetitionStandings(activeCompTab)
            .then(res => setStandingsCache(prev => ({...prev, [activeCompTab]: res.results as IStandingRow[]})))
            .catch(() => setStandingsCache(prev => ({...prev, [activeCompTab]: []})))
            .finally(() => setLoadingStandings(false));
    }, [activeTab, activeCompTab, standingsCache]);

    if (dialog.name !== "league") return null;

    // ── Public league rendering ──────────────────────────────────────────────
    const isPublicTab = activeTab !== "myLeagues";
    const data = isPublicTab ? cache[activeTab as LeagueId] : undefined;
    const standings = data?.results ?? [];
    const tabMeta = PUBLIC_TABS.find(t => t.id === activeTab);
    const currentNickname = game.userPlayer?.getNickname();

    const currentIndex = currentNickname
        ? standings.findIndex(r => Player.MakeNickname(r.nickname, r.nick_number) === currentNickname)
        : -1;

    const top3End = Math.min(3, standings.length);
    let sections: {rows: IndexedRow[], showSeparator: boolean}[] = [];

    if (isPublicTab && standings.length > 0) {
        const indexed = (slice: LeagueRow[], offset: number): IndexedRow[] =>
            slice.map((r, i) => ({...r, index: i + offset}));

        if (currentIndex === -1) {
            sections = [{rows: indexed(standings.slice(0, top3End), 0), showSeparator: false}];
        } else {
            const winLow  = Math.max(0, currentIndex - 3);
            const winHigh = Math.min(standings.length - 1, currentIndex + 3);

            if (winLow <= 2) {
                const end = Math.max(top3End, winHigh + 1);
                sections = [{rows: indexed(standings.slice(0, end), 0), showSeparator: false}];
            } else {
                sections = [
                    {rows: indexed(standings.slice(0, top3End), 0),               showSeparator: false},
                    {rows: indexed(standings.slice(winLow, winHigh + 1), winLow), showSeparator: true},
                ];
            }
        }
    }

    const handleWordClick = (row: {best_word: string | null; best_word_region_id: number | null; best_word_tile_id: number | null}) => {
        if (!row.best_word) return;
        const position = row.best_word_region_id != null && row.best_word_tile_id != null
            ? game.size.getTilePosition({regionId: row.best_word_region_id, tileId: row.best_word_tile_id})
            : undefined;
        updateDialog({update: {name: "definition", data: {word: row.best_word, position: position ? {x: position.x, y: position.y} : undefined}}});
    };

    const renderPublicRow = (row: IndexedRow) => {
        const isCurrent = Player.MakeNickname(row.nickname, row.nick_number) === currentNickname;
        return (
            <tr key={row.index} style={isCurrent ? styles.currentRow : {}}>
                <td style={{...styles.td, textAlign: "center", color: COLOURS.amber, fontWeight: "bold"}}>{row.index + 1}</td>
                <td style={{...styles.td, ...styles.tdTruncate}}>{Player.MakeNickname(row.nickname, row.nick_number)}</td>
                <td style={{...styles.td, textAlign: "right"}}>{row.committed.toLocaleString()}</td>
                <td style={{...styles.td, textAlign: "right", fontWeight: "bold"}}>{row.total.toLocaleString()}</td>
                <td onClick={() => handleWordClick(row)}
                    style={{...styles.td, ...styles.tdTruncate, fontStyle: "italic", cursor: row.best_word ? "pointer" : undefined}}>
                    {row.best_word ?? "—"}
                </td>
                <td style={{...styles.td, textAlign: "right", color: COLOURS.amber}}>
                    {row.best_word_score != null ? row.best_word_score : ""}
                </td>
            </tr>
        );
    };

    // ── My Leagues helpers ───────────────────────────────────────────────────
    const handleShare = (code: string, compName: string) => {
        const url = `${SITE_ORIGIN}/?join=${code}`;
        navigator.share({ title: compName, url });
    };

    const handleCreate = async () => {
        if (!createName.trim()) return;
        setCreating(true);
        setCreateError(null);
        try {
            const result = await game.createCompetition(createName.trim());
            const res = await game.getPlayerCompetitions();
            setCompetitions(res.results);
            setNewLeagueId(result.id);
            setCreateName("");
            setActiveCompTab(result.id);
        } catch {
            setCreateError("Failed to create competition. Please try again.");
        } finally {
            setCreating(false);
        }
    };

    const activeComp = competitions.find(c => c.id === activeCompTab);
    const compStandings = activeCompTab !== "new" ? (standingsCache[activeCompTab] ?? []) : [];

    const dialogTitle = activeTab === "myLeagues" ? "My Leagues" : (data?.league?.name ?? "League");
    const dialogSubtitle = activeTab === "myLeagues"
        ? "Leagues to which you have been invited or can invite others"
        : (data?.league?.subtitle ?? "");

    return (
        <Dialog title={dialogTitle} subtitle={dialogSubtitle} close>
            <div style={styles.body}>
                {/* ── Top-level tabs ── */}
                <div style={styles.tabs}>
                    {PUBLIC_TABS.map(tab => (
                        <div
                            key={tab.id}
                            style={{...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {})}}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </div>
                    ))}
                    {!game.userPlayer?.anonymous && (
                        <div
                            style={{...styles.tab, ...(activeTab === "myLeagues" ? styles.tabActive : {})}}
                            onClick={() => setActiveTab("myLeagues")}
                        >
                            My Leagues
                        </div>
                    )}
                </div>

                {/* ── Public league scroll area ── */}
                {isPublicTab && (
                    <div style={styles.scroll}>
                        {loading && <div style={styles.empty}>Loading…</div>}
                        {!loading && standings.length === 0 && (
                            <div style={styles.empty}>{tabMeta?.emptyMsg}</div>
                        )}
                        {!loading && standings.length > 0 && (
                            <div style={{overflowX: "auto"}}>
                                <table style={styles.table}>
                                    <colgroup>
                                        <col style={{width: 28}}/>
                                        <col/>
                                        <col style={{width: 52}}/>
                                        <col style={{width: 56}}/>
                                        <col/>
                                        <col style={{width: 40}}/>
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={{...styles.th, textAlign: "center"}}>#</th>
                                            <th style={styles.th}>Player</th>
                                            <th style={{...styles.th, textAlign: "right"}}>Score</th>
                                            <th style={{...styles.th, textAlign: "right"}}>Current</th>
                                            <th style={styles.th}>Best word</th>
                                            <th style={styles.th}/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sections.map((section, si) => (
                                            <React.Fragment key={si}>
                                                {section.showSeparator && (
                                                    <tr><td colSpan={6} style={styles.separator}>· · ·</td></tr>
                                                )}
                                                {section.rows.map(renderPublicRow)}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ── My Leagues: sub-tabs + scroll + footer ── */}
                {!isPublicTab && (
                    <>
                        {/* Sub-tab bar (outside scroll, scrollable itself with arrow buttons) */}
                        {!loadingComps && (
                            <div style={styles.compSubTabsWrapper}>
                                {canScrollLeft && (
                                    <button style={styles.compScrollBtn} onClick={() => scrollTabs("left")}>‹</button>
                                )}
                                <div ref={tabBarRef} style={styles.compSubTabs}>
                                    <div
                                        style={{...styles.compTab, ...(activeCompTab === "new" ? styles.compTabActive : {})}}
                                        onClick={() => setActiveCompTab("new")}
                                    >
                                        + New League
                                    </div>
                                    {competitions.map(comp => (
                                        <div
                                            key={comp.id}
                                            style={{
                                                ...styles.compTab,
                                                ...(activeCompTab === comp.id ? styles.compTabActive : {}),
                                                ...(newLeagueId === comp.id ? {color: COLOURS.forestGreen} : {})
                                            }}
                                            onClick={() => setActiveCompTab(comp.id)}
                                        >
                                            {comp.name}
                                        </div>
                                    ))}
                                </div>
                                {canScrollRight && (
                                    <button style={styles.compScrollBtn} onClick={() => scrollTabs("right")}>›</button>
                                )}
                            </div>
                        )}

                        {/* Scrollable content area */}
                        <div style={styles.scroll}>
                            {loadingComps && <div style={styles.empty}>Loading…</div>}

                            {/* New League form */}
                            {!loadingComps && activeCompTab === "new" && !game.userPlayer?.anonymous && (
                                <div style={styles.createForm}>
                                    <input
                                        style={styles.createInput}
                                        type="text"
                                        placeholder="League name"
                                        value={createName}
                                        onChange={e => setCreateName(e.target.value)}
                                        maxLength={60}
                                    />
                                    <button
                                        style={{...THEME.button, alignSelf: "flex-start", margin: 0}}
                                        onClick={handleCreate}
                                        disabled={creating || !createName.trim()}
                                    >
                                        {creating ? "Creating…" : "Create"}
                                    </button>
                                    {createError && <div style={styles.createError}>{createError}</div>}
                                </div>
                            )}

                            {/* Competition standings */}
                            {!loadingComps && activeCompTab !== "new" && activeComp && (
                                <div style={{overflowX: "auto"}}>
                                    {loadingStandings && <div style={styles.empty}>Loading…</div>}
                                    {!loadingStandings && (
                                        <table style={styles.table}>
                                            <colgroup>
                                                <col style={{width: 28}}/>
                                                <col/>
                                                <col style={{width: 52}}/>
                                                <col style={{width: 56}}/>
                                                <col/>
                                                <col style={{width: 40}}/>
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th style={{...styles.th, textAlign: "center"}}>#</th>
                                                    <th style={styles.th}>Player</th>
                                                    <th style={{...styles.th, textAlign: "right"}}>Score</th>
                                                    <th style={{...styles.th, textAlign: "right"}}>Current</th>
                                                    <th style={styles.th}>Best word</th>
                                                    <th style={styles.th}/>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {compStandings.map((row, i) => {
                                                    const isCurrent = Player.MakeNickname(row.nickname, row.nick_number) === currentNickname;
                                                    return (
                                                        <tr key={i} style={isCurrent ? styles.currentRow : {}}>
                                                            <td style={{...styles.td, textAlign: "center", color: COLOURS.amber, fontWeight: "bold"}}>{i + 1}</td>
                                                            <td style={{...styles.td, ...styles.tdTruncate}}>{Player.MakeNickname(row.nickname, row.nick_number)}</td>
                                                            <td style={{...styles.td, textAlign: "right"}}>{row.committed.toLocaleString()}</td>
                                                            <td style={{...styles.td, textAlign: "right", fontWeight: "bold"}}>{row.total.toLocaleString()}</td>
                                                            <td onClick={() => handleWordClick(row)}
                                                                style={{...styles.td, ...styles.tdTruncate, fontStyle: "italic", cursor: row.best_word ? "pointer" : undefined}}>
                                                                {row.best_word ?? "—"}
                                                            </td>
                                                            <td style={{...styles.td, textAlign: "right", color: COLOURS.amber}}>
                                                                {row.best_word_score != null ? row.best_word_score : ""}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {compStandings.length === 0 && (
                                                    <tr><td colSpan={6} style={styles.empty}>No scores yet.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer: end date + share link, pinned below the table */}
                        {!loadingComps && activeCompTab !== "new" && activeComp && (
                            <div style={styles.compFooter}>
                                <span style={styles.compMetaText}>Ends {activeComp.ends_at}</span>
                                <span>
                                    <button
                                        style={{...THEME.button}}
                                        onClick={() => handleShare(activeComp.invite_code, activeComp.name)}
                                    >
                                        Share link
                                    </button>
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
}

const mapStateToProps = (state: never) => ({dialog: selectDialog(state)});
const connected = connect(mapStateToProps, {updateDialog})(LeagueDialog);
export {connected as LeagueDialog};
